// Bring In From Field Labels (BRING_IN_B3_WORDS.md, mockup MOCKUP_BRING_IN.html, approved 2026-10-28 10:03 AM).
// Pure: the walk in, new Creator rows out. The Creator lines them up as a run, the same as Copy Down To, and each
// row saves when its voltage, conductor and four answers are filled. Rows already on the sheet are never touched.
//
// What becomes a row (the workbook's rows, as the Baconton registry shows them): every piece of gear, the housing;
// and every bucket or compartment of sectioned gear, spares included (Robert, 10:00 AM), with its position.
// Panel breakers and MB are not rows: a panel is one asset.

import { assetIdOf, BLANK_FORM, type ClassOption, type IdForm } from "./asset-id";
import { gearLabels, locationOf, roomLetters, type FieldSite } from "./field-labels-site";

export interface BringIn {
  /** New rows, in walk order: room by room, top of the room down, each cabinet then its positions. */
  rows: IdForm[];
  /** New rows per room, rooms in walk order. Rooms with none are left out. */
  byRoom: { room: string; count: number }[];
  /** Asset IDs the walk makes that are already on the sheet. Left exactly as they are. */
  already: number;
  /** Gear whose type has no Equipment Class. Skipped, buckets and all, so the class can be added first. */
  skipped: { room: string; gear: string; type: string }[];
}

/** One Asset ID the walk makes, and where in the walk it comes from. */
export interface WalkRow {
  id: string;
  room: string;
  gearId: string;
  /** "" for the housing, "A1" for a bucket. */
  pos: string;
  form: IdForm;
}

/** The Creator's Equipment Location for a walk room code. */
const LOCATION_NAMES: Record<string, string> = { ER: "Electrical Room", EA: "Electrical Area", UTL: "Outside" };

/**
 * Every Asset ID the walk makes, in walk order, with its room, gear and position (ASSESSMENT_FLOW_B7_PART2_WORDS.md).
 * Bring In, the Field Labels stamp on a saved row, the On Sheet lock and the push's removals all read this one list.
 */
export function walkRowsOf(site: FieldSite, siteNumber: string, classes: readonly ClassOption[]): { rows: WalkRow[]; skipped: BringIn["skipped"] } {
  const rows: WalkRow[] = [];
  const skipped: BringIn["skipped"] = [];
  const seen = new Set<string>();
  for (const r of site.rooms) {
    const where = locationOf(r.name);
    const loc = LOCATION_NAMES[where.location];
    if (!loc) continue;
    const letters = roomLetters(r);
    for (const g of r.gear) {
      const cls = classes.find((c) => c.code === g.type);
      if (!cls) {
        skipped.push({ room: r.name, gear: g.name, type: g.type });
        continue;
      }
      const num = /^[A-Z]+-(.+)$/.exec(g.name)?.[1] ?? "";
      // Shock Only · 120 V on the walk: the voltage comes in set (Robert, 10:52 AM).
      const base: IdForm = { ...BLANK_FORM, tag: g.tag ?? "", loc, locNum: where.locNumber, type: cls.name, num, volt: g.shockOnly ? "120V (1-P)" : "" };
      const positions = gearLabels(r, g, letters).filter((l) => l.kind === "bucket").map((l) => l.text);
      for (const pos of ["", ...positions]) {
        const form: IdForm = { ...base, pos };
        const id = assetIdOf(siteNumber, form, classes);
        if (!id || seen.has(id)) continue;
        seen.add(id);
        rows.push({ id, room: r.name, gearId: g.id, pos, form });
      }
    }
  }
  return { rows, skipped };
}

export function bringIn(site: FieldSite, siteNumber: string, classes: readonly ClassOption[], existing: ReadonlySet<string>): BringIn {
  const { rows, skipped } = walkRowsOf(site, siteNumber, classes);
  const out: BringIn = { rows: [], byRoom: [], already: 0, skipped };
  for (const w of rows) {
    if (existing.has(w.id)) {
      out.already += 1;
      continue;
    }
    out.rows.push(w.form);
    const last = out.byRoom[out.byRoom.length - 1];
    if (last && last.room === w.room) last.count += 1;
    else out.byRoom.push({ room: w.room, count: 1 });
  }
  return out;
}
